import {observer} from "mobx-react";
import {IReactiveNewsContentItem, ReactiveContentItem} from "../../schoolfeed/SchoolFeedContentItem";
import React, {useRef, useState} from "react";
import styles from "./styles/NewsModal.module.scss";
import socialItemModalStyles from "../styles/SocialItemModal.module.scss";
import {UnpublishedInfoBox} from "../../utilities/UnpublishedInfoBox";
import ImageComponent from "../../utilities/ImageComponent";
import {EditButton} from "../../blocks/TerracedGridTheme/EditButton";
import Moment from "moment";
import {ReadMoreButton} from "../SocialItemModal";
import {MeritChatThread} from "../../utilities/MeritChat";
import ClickableLink from "../../utilities/ClickableLink";
import {buildCleverFeedUrl, buildContentItemUrl} from "../../../utils/SchoolBlocksUtilities";
import {JoinDiscussionButton} from "../../schoolfeed/ContentPostDisplay/JoinDiscussionButton";
import {unpublishItem} from "../../schoolfeed/ContentPostDisplay/utils";
import NewsModalContentHeader from "./NewsModalContentHeader";
import {useStores} from "../../../stores/useStores";
import classNames from "classnames";
import {useTranslateClassName} from "../../../hooks/useTranslateClassName";
import Markdown from "../../utilities/Markdown";
import {useFeatureFlags} from "../../../utils/featureFlags/useFeatureFlags";
import WatsonApi from "../../../backends/WatsonApi";
import NotificationManager from "../../notifications/NotificationManager";
import SchoolBlocksLoadingIndicator from "../../utilities/SchoolBlocksLoadingIndicator";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import {TranslateButton} from "../../schoolfeed/ContentPostDisplay/TranslateButton";

export const meritChatID = "meritChatFeed"

export function UnpublishedContentItemInfoBox({unpublishedItemType, item}) {
    return <UnpublishedInfoBox
        unpublishedItemType={unpublishedItemType}
        handleClick={() => unpublishItem(item)}
    />
}

const NewsModalContent = observer((props: {
    index: number,
    itemObj: ReactiveContentItem,
    handleDelete: (contentItem: ContentItem) => void,
    navigationButtons?: React.ReactElement,
    closeFunction?: () => void,
    className?: string,
}) => {
    const {show_meritocracy_chat} = useFeatureFlags();
    const {userStore, organizationStore} = useStores();
    const imageUrl = props.itemObj.image;
    const contentRef = useRef<HTMLDivElement>(null);
    const shareUrlPath = buildContentItemUrl(props.itemObj.organization_id, props.itemObj.id);
    const {show_translate_button} = useFeatureFlags();

    const [description, setDescription] = useState("");

    const containerClassName = classNames({
        [styles.container]: true,
        [props.className || ""]: props.className,
    })
    const translateClassName = useTranslateClassName();

    const getDescription = async () => {
        try {
            const client = await WatsonApi();
            const result = await client.apis.organizations.organizations_content_items_read({
                organization_pk: props.itemObj.organization_id,
                id: props.itemObj.id,
                expand: ["organization"],
                html_with_styles: true,
            });

            return result.obj.description;
        } catch (e) {
            NotificationManager.error(e.response?.body?.detail);
        }
    }

    useAsyncEffect(async () => {
        const description: string = await getDescription();
        setDescription(description);
    },[props.itemObj.id])

    return <div ref={contentRef} className={containerClassName}>
        {!props.itemObj.published && <UnpublishedContentItemInfoBox unpublishedItemType={"News Item"} item={props.itemObj}/>}
        <NewsModalContentHeader index={props.index}
                                closeFunction={props.closeFunction}
                                itemObj={props.itemObj}
                                navigationButtons={props.navigationButtons}
                                shareUrlPath={shareUrlPath}
                                printRef={contentRef}/>
        <article>
            {imageUrl && <ImageComponent
                src={imageUrl}
                key={props.itemObj.id}
                alt={props.itemObj.title || "Social Post Image"}
            />}
            <div className={styles.titleContainer}>
                <h2 className={translateClassName}>
                    <Markdown withStyles={false} source={props.itemObj.title}></Markdown>
                </h2>
                {userStore.isEditor && <EditButton contentItem={props.itemObj as IReactiveNewsContentItem}
                                                   handleDelete={props.handleDelete}/>}
                {show_translate_button && <TranslateButton itemObj={props.itemObj as IReactiveNewsContentItem} />}
            </div>
            <div className={styles.subheader}>
                <small>Posted on <span
                    className={styles.postDate}>{Moment(props.itemObj.created_at).format("MMM D, YYYY")}</span></small>
                <small>
                    <ClickableLink aria-label={`Link to ${props.itemObj.organization.title}'s News Feed`}
                                   title={`Link to ${props.itemObj.organization.title}'s News Feed`}
                                   href={buildCleverFeedUrl(props.itemObj.organization.id, 'news')}>{`${props.itemObj.organization.title} News`}</ClickableLink></small>
            </div>
            {description ? <div className={styles.content}>
                <Markdown withStyles={true} className={translateClassName} source={description}/>
            </div> : <div className={styles.loadingIndicator}><SchoolBlocksLoadingIndicator/></div>}
            {!show_meritocracy_chat &&
                organizationStore.schoolFeedEnabledForOrganization &&
                props.itemObj.meritchat_enabled &&
                <div style={{marginBottom: 10}}>
                    <JoinDiscussionButton className={socialItemModalStyles.viewBtn} itemObj={props.itemObj as IReactiveNewsContentItem}/>
                </div>}
            {props.itemObj.link && <ReadMoreButton itemObj={props.itemObj}/>}
            <div className="clearfix"/>
        </article>
        {show_meritocracy_chat && props.itemObj.meritchat_enabled && <div
            style={{
            clear: "left",
            }}
            id={meritChatID}
        >
            <MeritChatThread
                source_id={props.itemObj.id}
                organization_id={props.itemObj.organization_id}
                source_category={props.itemObj.type}
            />
        </div>}
    </div>
})

export default NewsModalContent;
